import { AlertTriangle, ShieldCheck, CircleGauge, Wrench, Euro, ChevronRight, Store, Clock } from "lucide-react";

const SEV = {
  basso: { label: "Gravita Bassa", color: "#10B981", bg: "rgba(16,185,129,0.12)", icon: ShieldCheck },
  medio: { label: "Gravita Media", color: "#F59E0B", bg: "rgba(245,158,11,0.12)", icon: CircleGauge },
  critico: { label: "Critico - Non guidare!", color: "#EF4444", bg: "rgba(239,68,68,0.12)", icon: AlertTriangle },
};

function Bar({ value, color }) {
  return (
    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${value}%`, backgroundColor: color }} />
    </div>
  );
}

export default function DiagnosisResult({ result }) {
  if (!result) return null;
  const sev = SEV[result.severity] || SEV.medio;
  const SevIcon = sev.icon;

  return (
    <div className="space-y-6" data-testid="diagnosis-result">
      <div
        data-testid="diagnostic-severity-badge"
        className="flex items-center gap-3 rounded-xl p-4 border"
        style={{ backgroundColor: sev.bg, borderColor: sev.color + "55" }}
      >
        <SevIcon className="w-6 h-6 shrink-0" style={{ color: sev.color }} />
        <div>
          <div className="font-display font-bold text-lg leading-none" style={{ color: sev.color }}>
            {sev.label}
          </div>
          <p className="text-sm text-zinc-300 mt-1">{result.summary}</p>
        </div>
      </div>

      {result.warning ? (
        <div className="flex items-start gap-2 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/40 p-3 text-sm text-red-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-[#EF4444]" />
          {result.warning}
        </div>
      ) : null}

      <div>
        <h3 className="font-display font-bold text-lg mb-3 tracking-wide uppercase text-zinc-200">Cause Probabili</h3>
        <div className="space-y-3">
          {(result.causes || []).map((c, i) => (
            <div key={i} className="rounded-lg bg-[#1A2232] border border-white/5 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-zinc-100">{c.cause}</span>
                <span className="font-mono text-sm font-bold text-[#06B6D4]">{c.confidence}%</span>
              </div>
              <Bar value={c.confidence} color={i === 0 ? "#F97316" : "#06B6D4"} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold text-lg mb-3 tracking-wide uppercase text-zinc-200">Passi per la Risoluzione</h3>
        <div className="space-y-3">
          {(result.steps || []).map((s, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-7 h-7 shrink-0 rounded-lg bg-[#F97316]/15 text-[#F97316] font-mono font-bold text-sm flex items-center justify-center">
                {i + 1}
              </div>
              <div className="flex-1 rounded-lg bg-[#1A2232] border border-white/5 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ChevronRight className="w-4 h-4 text-[#F97316]" />
                  <span className="font-semibold text-sm text-zinc-100">{s.title}</span>
                  <span
                    className={`ml-auto text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                      s.type === "officina" ? "bg-[#EF4444]/15 text-red-300" : "bg-[#10B981]/15 text-emerald-300"
                    }`}
                  >
                    {s.type}
                  </span>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {result.parts?.length > 0 && (
        <div data-testid="parts-price-section">
          <h3 className="font-display font-bold text-lg mb-1 tracking-wide uppercase text-zinc-200 flex items-center gap-2">
            <Store className="w-4 h-4 text-[#F97316]" /> Confronto Officine
          </h3>
          <p className="text-xs text-zinc-500 mb-3">Prezzi indicativi dei ricambi (aftermarket) per trattare meglio dal meccanico.</p>
          <div className="rounded-lg bg-[#1A2232] border border-white/5 overflow-hidden">
            {result.parts.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 last:border-0">
                <span className="text-sm text-zinc-200">{p.name}</span>
                <span className="font-mono text-sm font-bold text-[#06B6D4] shrink-0 ml-3">€{p.price_min} - €{p.price_max}</span>
              </div>
            ))}
            {result.labor_estimate && (
              <div className="flex items-center justify-between px-4 py-2.5 bg-white/[0.03]">
                <span className="text-sm text-zinc-300 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-zinc-500" /> Manodopera
                  {result.labor_hours ? <span className="text-xs text-zinc-500">({result.labor_hours})</span> : null}
                </span>
                <span className="font-mono text-sm font-bold text-zinc-200 shrink-0 ml-3">{result.labor_estimate}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-lg bg-[#1A2232] border border-white/5 p-4">
          <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-2">
            <Wrench className="w-4 h-4" /> Strumenti
          </div>
          <div className="flex flex-wrap gap-2">
            {(result.tools || []).map((t, i) => (
              <span key={i} className="text-xs font-mono px-2 py-1 rounded bg-white/5 text-zinc-200">{t}</span>
            ))}
          </div>
        </div>
        <div className="rounded-lg bg-[#1A2232] border border-white/5 p-4">
          <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-2">
            <Euro className="w-4 h-4" /> Stima Totale
          </div>
          <div className="font-display font-black text-2xl text-[#F97316]">{result.estimated_cost}</div>
        </div>
      </div>
    </div>
  );
}
